import { FormGroup } from "@angular/forms";
import {
    Component,
    OnInit,
    AfterViewInit,
    Input,
    Output,
    EventEmitter,
    OnChanges,
    SimpleChanges
} from "@angular/core";
import { MorePatientInfoService } from "src/app/services/more-patientinfo.service";
import { ModalWrapperService } from "src/app/services";
import { RegEx } from "src/app/app.regex";
import * as moment from "moment";
import { PatientStore } from "src/app/store";

@Component({
    selector: "app-facility-info",
    templateUrl: "./facility-info.component.html",
    styles: [],
    standalone: false
})
export class PFacilityInfoComponent implements OnInit, AfterViewInit, OnChanges {
    @Input() patientId: any;
    @Input() facilityFG: FormGroup;
    @Input() facilityFGs: FormGroup[] = [];
    @Input() facilityInfo: any;
    @Input() focusId: any;
    @Output() SaveFacilityRow = new EventEmitter<{index: number}>();
    @Output() CreateAdmission = new EventEmitter<void>();
    @Output() CancelNewFacilityRow = new EventEmitter<{index: number}>();
    @Output() DeleteFacilityRow = new EventEmitter<{index: number}>();
    regex = RegEx;
    expandedIndex: number = -1;
    _facilityGroups: FormGroup[] = [];
    prescInputValues: string[] = [];
    locationsByIndex: any[] = [];
    stationsByIndex: any[] = [];
    savingRows: boolean[] = [];
    dischargeRequiredRows: boolean[] = [];
    editingRows: boolean[] = [];
    editSnapshots: { [index: number]: any } = {};
    rowStates: { isNew: boolean; hasFacility: boolean; discharged: boolean; disabled: boolean; editing: boolean; statusText: string; statusClass: string; }[] = [];
    facilityCodeOptions: { Id: number; FacilityCode: string; FacilityName: string }[] = [];
    filterFacilityId: number = -1;
    filteredGroups: { fg: FormGroup; originalIndex: number }[] = [];
    today: string;
    maxDate: { year: number; month: number; day: number; };
    deleteConfirmIndex: number = -1;
    deleteModalRef: any;

    constructor(
        private _morePatInfoSvc: MorePatientInfoService,
        private _patientStore: PatientStore,
        private _modalSvc: ModalWrapperService
    ) {}

    ngOnInit() {
        this.today = moment().format("MM/DD/YYYY");
        this.maxDate = {
            year: moment(this.today).year(),
            month: moment(this.today).month() + 1,
            day: moment(this.today).date()
        };
        this.updateFacilityGroups();
        this.initializeFacilityRows();
    }

    ngOnChanges(changes: SimpleChanges) {
        const groupsChanged = !!(changes["facilityFGs"] || changes["facilityFG"]);
        if (groupsChanged) {
            this.updateFacilityGroups();
            this.initializeFacilityRows(true);
            return;
        }

        if (changes["facilityInfo"]) {
            this._facilityGroups.forEach((fg, index) => {
                this.setLocationAndStation(this.facilityInfo, index, fg.value["FacilityId"]);
                this.prescInputValues[index] = fg.value["PrimePrescName"] || "";
            });
        }
    }

    ngAfterViewInit() {
        if (this.focusId) {
            const element: any = document.getElementById(this.focusId);
            if (element)
                element.focus();
        } else if (this._facilityGroups.length > 0 && document.getElementById("Facility0")) {
            const data: any = document.getElementById("Facility0").getElementsByTagName("input")[0];
            if (data) {
                data.focus();
            }
        }
    }

    get facilityGroups(): FormGroup[] {
        return this._facilityGroups;
    }

    private updateFacilityGroups() {
        this._facilityGroups = this.facilityFGs && this.facilityFGs.length > 0
            ? this.facilityFGs
            : this.facilityFG ? [this.facilityFG] : [];
        this.filteredGroups = this._facilityGroups.map((fg, i) => ({ fg, originalIndex: i }));
    }

    initializeFacilityRows(collapseAllRows: boolean = false) {
        const prevSavingRows = [...this.savingRows];
        const prevDischargeRequiredRows = [...this.dischargeRequiredRows];
        this._facilityGroups.forEach((fg, index) => this.setDefaultValues(fg, index));
        if (collapseAllRows) {
            this.expandedIndex = -1;
        }
        this.savingRows = this._facilityGroups.map((_, index) => prevSavingRows[index] || false);
        this.dischargeRequiredRows = this._facilityGroups.map((_, index) => prevDischargeRequiredRows[index] || false);
        this.editingRows = this._facilityGroups.map(() => false);
        this.editSnapshots = {};
        this.rowStates = this._facilityGroups.map((fg, index) => this.computeRowState(fg, index));
        this.buildFacilityCodeOptions();
        this.applyFacilityFilter();
    }

    toggleRow(index: number) {
        if (this.expandedIndex === index) {
            this.expandedIndex = -1;
            this.editingRows[index] = false;
            this.refreshRowState(index);
        } else {
            this.expandedIndex = index;
        }
    }

    editRow(index: number) {
        const fg = this._facilityGroups[index];
        if (fg) {
            this.editSnapshots[index] = { ...fg.value };
        }
        this.editingRows[index] = true;
        this.expandedIndex = index;
        this.refreshRowState(index);
    }

    isExpanded(index: number) {
        return this.expandedIndex === index;
    }

    trackByFacility(index: number, facilityFG: FormGroup) {
        return facilityFG && facilityFG.value && facilityFG.value["Id"] ? facilityFG.value["Id"] : index;
    }

    trackByFilteredItem(index: number, item: { fg: FormGroup; originalIndex: number }) {
        return item.fg && item.fg.value && item.fg.value["Id"] ? item.fg.value["Id"] : item.originalIndex;
    }

    setDefaultValues(facilityFG: FormGroup, index: number) {
        this.setLocationAndStation(this.facilityInfo, index, facilityFG.value["FacilityId"]);
        facilityFG.patchValue({
            PatientId: this.patientId,
            IsActive: facilityFG.value["IsActive"] !== null ? facilityFG.value["IsActive"] : true,
            IsCycleFillAllowed: facilityFG.value["IsCycleFillAllowed"] !== null ? facilityFG.value["IsCycleFillAllowed"] : true,
            IsUnitDoseAllowed: facilityFG.value["IsUnitDoseAllowed"] !== null ? facilityFG.value["IsUnitDoseAllowed"] : false,
            IsPrintMedSheet: facilityFG.value["IsPrintMedSheet"] !== null ? facilityFG.value["IsPrintMedSheet"] : false,
            FacLocId: facilityFG.value["FacLocId"] ? facilityFG.value["FacLocId"] : 0,
            FacStationId: facilityFG.value["FacStationId"] ? facilityFG.value["FacStationId"] : 0,
        });
        this.prescInputValues[index] = facilityFG.value["PrimePrescName"] || "";
    }

    setLocationAndStation(data, index: number, facilityId?: number) {
        let loca = [{ Id: 0, Name: "All" }];
        let stat = [{ Id: 0, Name: "All" }];
        if (data && data["FacLocation"] && data["FacLocation"]["length"] > 0) {
            if (facilityId) {
                loca = loca.concat(data["FacLocation"].filter(loc => loc.FacilityId === facilityId));
            }
            else {
                loca = loca.concat(data["FacLocation"]);
            }
        }
        if (data && data["FacStation"] && data["FacStation"]["length"] > 0) {
            if (facilityId) {
                stat = stat.concat(data["FacStation"].filter(st => st.FacilityId === facilityId));
            }
            else {
                stat = stat.concat(data["FacStation"]);
            }
        }
        this.locationsByIndex[index] = Object.assign([], loca);
        this.stationsByIndex[index] = Object.assign([], stat);
    }

    getLocations(index: number) {
        return this.locationsByIndex[index] ? this.locationsByIndex[index] : [{Id: 0, Name: "All"}];
    }

    getStations(index: number) {
        return this.stationsByIndex[index] ? this.stationsByIndex[index] : [{Id: 0, Name: "All"}];
    }

    selectedPresInfo(val, index: number, facilityFG: FormGroup) {
        if (val && val.prescriberid) {
            this.prescInputValues[index] = val.prescriberlastname + (val.prescriberfirstname ? ", " + val.prescriberfirstname : "");
            facilityFG.controls["PrimPrescbId"].setValue(val.prescriberid);
            facilityFG.controls["PrimePrescName"].setValue(this.prescInputValues[index]);
        } else {
            facilityFG.controls["PrimPrescbId"].setValue(null);
            facilityFG.controls["PrimePrescName"].setValue(null);
            this.prescInputValues[index] = "";
        }
        facilityFG.markAsDirty();
    }

    async getFacLoc(index: number, facilityFG: FormGroup) {
        if (!facilityFG.value["FacilityId"]) {
            this.setLocationAndStation({}, index);
            facilityFG.patchValue({
                FacLocId: null,
                FacStationId: null
            });
            return;
        }
        const response = await this._morePatInfoSvc.getFacilityLocations(facilityFG.value["FacilityId"]).toPromise();
        if (this.facilityInfo && response) {
            this.facilityInfo["FacLocation"] = response["FacLocation"];
            this.facilityInfo["FacStation"] = response["FacStation"];            
        }
        this._patientStore.storeFacilty(Object.assign({}, this.facilityInfo));
        this.setLocationAndStation(response, index, facilityFG.value["FacilityId"]);
        facilityFG.patchValue({
                FacLocId: null,
                FacStationId: null
            });
        this.refreshRowState(index);
    }

    requestSaveFacility(index: number, facilityFG: FormGroup) {
        if (!facilityFG || this.savingRows[index] || !this.canSave(facilityFG)) {
            return;
        }
        const state = this.rowStates[index];
        let hasValidationError = false;
        if (!this.hasFacilityCode(facilityFG)) {
            const facilityCodeCtrl = facilityFG.controls["FacilityId"];
            this.setControlError(facilityCodeCtrl, "required");
            this.focusElement("Facility" + index);
            hasValidationError = true;
        }

        const admitDate = moment(facilityFG.value["AdmitDt"], ["MM/DD/YYYY", moment.ISO_8601], true);
        if (!facilityFG.value["AdmitDt"] || !admitDate.isValid()) {
            const admitDateCtrl = facilityFG.controls["AdmitDt"];
            this.setControlError(admitDateCtrl, "required");
            if (!hasValidationError) {
                this.focusElement("AdmitDtID" + index);
            }
            hasValidationError = true;
        }
        if(!this.hasActiveFacility() && state && state.isNew && this.dischargeRequiredRows[index]) {
            this.dischargeRequiredRows[index] = false;
        }

        if (this.isDischargeDateRequired(index, facilityFG)) {
            const dischargeDate = moment(facilityFG.value["DischargeDt"], ["MM/DD/YYYY", moment.ISO_8601], true);
            if (!facilityFG.value["DischargeDt"] || !dischargeDate.isValid()) {
                const dischargeDateCtrl = facilityFG.controls["DischargeDt"];
                this.setControlError(dischargeDateCtrl, dischargeDate.isValid() ? "required" : "inValid");
                if (!hasValidationError) {
                    this.focusElement("DischargeDtID" + index);
                }
                hasValidationError = true;
            }
        }

        if (hasValidationError) {
            return;
        }
        if (state && !state.isNew && !facilityFG.value["DischargeDt"]) {
            facilityFG.patchValue(
                {
                    DischargeDt: null,
                    IsDischarged: false
                });
        }
        this.savingRows[index] = true;
        this.SaveFacilityRow.emit({ index });
    }

    requestCreateAdmission() {
        this.filterFacilityId = -1;
        this.applyFacilityFilter();
        this.CreateAdmission.emit();
    }

    requestCancelNewRow(index: number, facilityFG: FormGroup) {
        if (!this.isNewRecord(facilityFG)) {
            if (this.editSnapshots[index]) {
                facilityFG.patchValue(this.editSnapshots[index]);
                facilityFG.markAsPristine();
                delete this.editSnapshots[index];
            }
            this.editingRows[index] = false;
            this.refreshRowState(index);
            this.expandedIndex = -1;
            return;
        }
        this.CancelNewFacilityRow.emit({ index });
    }

    requestDeleteRow(index: number) {
        this.DeleteFacilityRow.emit({ index });
    }

    requestDeleteRowConfirm(content: any, index: number) {
        if (!this.canEdit(index)) {
            return;
        }
        this.deleteConfirmIndex = index;
        this.deleteModalRef = this._modalSvc.open(content, {
            centered: true,
            keyboard: false,
            backdrop: false,
            windowClass: "large--content"
        });
    }

    confirmDeleteRow() {
        if (this.deleteConfirmIndex < 0) {
            return;
        }
        this.requestDeleteRow(this.deleteConfirmIndex);
        this.cancelDeleteConfirmation();
    }

    cancelDeleteConfirmation() {
        if (this.deleteModalRef) {
            this.deleteModalRef.close();
            this.deleteModalRef = null;
        }
        this.deleteConfirmIndex = -1;
    }

    openRow(index: number) {
        this.editingRows[index] = true;
        this.expandedIndex = index;
        this.refreshRowState(index);
    }

    setSavingRow(index: number, isSaving: boolean) {
        this.savingRows[index] = isSaving;
    }

    focusElement(elementId: string) {
        setTimeout(() => {
            const el = document.getElementById(elementId);
            if (el) {
                const innerInput = el.getElementsByTagName("input")[0];
                if (innerInput) {
                    innerInput.focus();
                } else {
                    el.focus();
                }
            }
        }, 0);
    }

    private setControlError(ctrl: any, errorKey: string) {
        if (!ctrl) { return; }
        const errors = { ...(ctrl.errors || {}), [errorKey]: true };
        ctrl.setErrors(errors);
        ctrl.markAsTouched();
    }

    private clearControlError(ctrl: any, errorKey: string) {
        if (!ctrl || !ctrl.errors || !ctrl.errors[errorKey]) { return; }
        const remaining = { ...ctrl.errors };
        delete remaining[errorKey];
        ctrl.setErrors(Object.keys(remaining).length > 0 ? remaining : null);
    }

    refreshRowState(index: number) {
        const fg = this._facilityGroups[index];
        if (fg) {
            this.rowStates[index] = this.computeRowState(fg, index);
        }
    }

    private buildFacilityCodeOptions() {
        const seen = new Map<number, { Id: number; FacilityCode: string; FacilityName: string }>();
        this._facilityGroups.forEach(fg => {
            const id = fg.value["FacilityId"];
            if (id && !seen.has(id)) {
                seen.set(id, {
                    Id: id,
                    FacilityCode: this.facilityInfo?.Facility?.find(f => f.Id === id)?.FacilityCode || "",
                    //FacilityCode: fg.value["FacilityCode"] || "",
                    FacilityName: fg.value["FacilityName"] || ""
                });
            }
        });
        this.facilityCodeOptions = [
            { Id: -1, FacilityCode: "All", FacilityName: "Facility" },
            ...Array.from(seen.values())
        ];
    }

    applyFacilityFilter() {
        if (!this.filterFacilityId || this.filterFacilityId === -1) {
            this.filteredGroups = this._facilityGroups.map((fg, i) => ({ fg, originalIndex: i }));
        } else {
            this.filteredGroups = this._facilityGroups
                .map((fg, i) => ({ fg, originalIndex: i }))
                .filter(item => item.fg.value["FacilityId"] === this.filterFacilityId);
        }
    }

    onFacilityFilterChange(event: any) {
        this.filterFacilityId = event?.value?.Id ?? -1;
        this.applyFacilityFilter();
    }

    private computeRowState(fg: FormGroup, index: number) {
        const id = fg && fg.value ? fg.value["Id"] : null;
        const isNew = !id || +id === 0;
        const hasFacility = !!(fg && fg.value && fg.value["FacilityId"]);
        const discharged = !!(fg && fg.value && fg.value["IsDischarged"]);
        const editing = this.editingRows[index] || false;
        const disabled = isNew ? !hasFacility : !editing;
        return {
            isNew,
            hasFacility,
            discharged,
            disabled,
            editing,
            statusText: isNew ? '--' : (discharged ? "Discharged" : "Admitted"),
            statusClass: isNew ? "" : (discharged ? "status-badge status-discharged" : "status-badge status-admitted")
        };
    }

    isNewRecord(facilityFG: FormGroup) {
        const id = facilityFG && facilityFG.value ? facilityFG.value["Id"] : null;
        return !id || +id === 0;
    }

    hasFacilityCode(facilityFG: FormGroup) {
        return !!(facilityFG && facilityFG.value && facilityFG.value["FacilityId"]);
    }

    isDischarged(facilityFG: FormGroup) {
        return !!(facilityFG && facilityFG.value && facilityFG.value["IsDischarged"]);
    }

    hasActiveFacility(): boolean {
        return this._facilityGroups.some(fg => fg.value && fg.value["IsDischarged"] === false && fg.value["Id"] && fg.value["Id"] > 0);
    }

    canSave(facilityFG: FormGroup):boolean {
        if (!facilityFG) {
            return true;
        }

        if (this.hasActiveFacility() && this.isNewRecord(facilityFG)) {
            if(facilityFG.value["DischargeDt"]){
                const dischargeDate = moment(facilityFG.value["DischargeDt"], ["MM/DD/YYYY", moment.ISO_8601], true);
                if (dischargeDate.isValid() && dischargeDate.isSameOrBefore(moment(), "day")) {
                    return true;
                }
            }
            return false;
        }
        return true;
    }

    canEdit(index: number) {
        const state = this.rowStates[index];
        return state && state.hasFacility;
    }

    isDischargeDateRequired(index: number, facilityFG: FormGroup) {
        const state = this.rowStates[index];
        if (state && !state.isNew && index === 0){
            return false;
        }
        if (state && state.discharged) {
            return true;
        }
        if (this.hasActiveFacility() && state && state.isNew) {
            return true;
        }
        return !!(this.dischargeRequiredRows[index] && !facilityFG.value["DischargeDt"]);
    }

    formatDate(dateValue: any) {
        return dateValue ? moment(dateValue).format("MM/DD/YYYY") : "--";
    }

    focusToFirst(event) {
        if (document.getElementById("SaveButton")) {
            event.preventDefault();
            setTimeout(() => {
                document.getElementById("SaveButton").focus();
            }, 10);

        }
    }
}
